import {
  Divider,
  IconButton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { useState } from 'react';

import { LoadingStateNode } from '@/app/(dashboard)/db/[id]/DBEntitiesTree';
import { useDAL } from '@/contexts/useDAL';
import { ApplicationsLogosWithAliases } from '@/data/inventory/data';

import type { LogsSpaceSchemasResponse } from './types';

import { CloudProviderIndicator } from '../cloudProviderIndicator';
import { FiltersPanelWrapper } from '../filters/filtersPanelWrapper';
import { CircleImage } from '../shared/circleImage';
import { Link } from '../shared/link';

const DBTreeItem = styled(TreeItem)({
  padding: '4px 0',
});

export const LogsSpaceSchemaBrowser = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const dal = useDAL();
  const { data, isLoading } = dal.logs.useListSchemas();

  const [expandedItems, setExpandedItems] = useState<string[]>([
    'loading_0',
    'loading_0_0',
    'loading_0_0_0',
  ]);

  return (
    <FiltersPanelWrapper
      title='Data source schemas'
      subtitle='Explore the structure of your connected data sources'
      onClose={onClose}
    >
      <Stack sx={{ margin: '12px 24px 0' }} gap='12px'>
        <SimpleTreeView
          selectedItems={null}
          expandedItems={expandedItems}
          onExpandedItemsChange={(event, items) => setExpandedItems(items)}
          sx={{
            '& .MuiTreeItem-content.Mui-focused': {
              backgroundColor: 'transparent',
              outline: 'none',
            },
            '& .MuiTreeItem-content.Mui-focused .MuiTreeItem-label': {
              backgroundColor: 'transparent',
            },

            '& .MuiTreeItem-content:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiTreeItem-content:hover .MuiTreeItem-label': {
              backgroundColor: 'transparent',
            },

            '& .MuiTreeItem-content.Mui-focused:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {isLoading && <LoadingStateNode dbs={2} schemas={1} tables={4} />}
          {!isLoading && (
            <Stack
              gap='16px'
              sx={{ marginY: '12px' }}
              divider={<Divider sx={{ margin: '0 -24px' }} />}
            >
              {data?.entries.map((resource) => (
                <ResourceNode key={resource.resourceId} resource={resource} />
              ))}
            </Stack>
          )}
        </SimpleTreeView>
      </Stack>
    </FiltersPanelWrapper>
  );
};

function ResourceNode({
  resource,
}: {
  resource: LogsSpaceSchemasResponse['entries'][number];
}) {
  return (
    <DBTreeItem
      itemId={`resource-${resource.resourceId}`}
      label={
        <CloudProviderIndicator
          cloudProvider={resource.cloudProvider}
          text={resource.resourceName}
        />
      }
    >
      {!!resource.schemas?.length && (
        <Stack
          sx={{ marginY: '12px' }}
          gap='12px'
          divider={<Divider sx={{ borderStyle: 'dashed' }} />}
        >
          {resource.schemas?.map((schema) => (
            <SchemaNode
              key={schema.schemaName + schema.path}
              resourceId={resource.resourceId}
              schema={schema}
            />
          ))}
        </Stack>
      )}
    </DBTreeItem>
  );
}

function SchemaNode({
  resourceId,
  schema,
}: {
  resourceId: string;
  schema: NonNullable<
    LogsSpaceSchemasResponse['entries'][0]['schemas']
  >[number];
}) {
  return (
    <DBTreeItem
      itemId={`resource-${resourceId}-schema-${schema.path + schema.schemaName}`}
      label={
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          paddingY='4px'
          gap='12px'
        >
          <Stack direction='row' alignItems='center' gap='8px'>
            <CircleImage
              alt={schema.appName}
              src={ApplicationsLogosWithAliases[schema.appName]}
            />
            <Stack gap='4px'>
              <Typography
                variant='body1'
                className='truncate'
                maxWidth='144px'
                title={schema.path}
              >
                {schema.path}
              </Typography>
              <Typography variant='subtitle2'>{schema.schemaName}</Typography>
            </Stack>
          </Stack>
          <Tooltip title='Open in database explorer'>
            <IconButton
              size='small'
              color='primary'
              LinkComponent={Link}
              href={`/logs/${resourceId}`}
              onClick={(event) => event.stopPropagation()}
            >
              <i className='material-symbols-frame-inspect-rounded' />
            </IconButton>
          </Tooltip>
        </Stack>
      }
    >
      {!!schema.columns?.length && (
        <Stack sx={{ marginY: '12px' }}>
          {schema.columns.map(
            (column) =>
              !column.private && (
                <DBTreeItem
                  key={column.name}
                  itemId={`resource-${resourceId}-schema-${schema.path + schema.schemaName}-column-${column.name}`}
                  label={
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                      className='w-full'
                    >
                      <Typography className='truncate max-w-[200px]'>
                        {column.name}
                      </Typography>
                      <Typography variant='body1'>{column.type}</Typography>
                    </Stack>
                  }
                />
              )
          )}
        </Stack>
      )}
    </DBTreeItem>
  );
}
